import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

import type { UseMutationResult } from '@tanstack/react-query';

import type { TranslateTextsPayload, TranslateTextsResponse } from '@/features/campaign-texts/types/campaign-texts.types';

import type { CountryGenResult } from '../shared/multiCountryBulkEdit.types';
import { countryGenResultHasTexts, translateCountryGenResult } from '../shared/translateBulkEditCountryTexts';

export type BulkEditTranslateMutation = UseMutationResult<
  TranslateTextsResponse,
  Error,
  TranslateTextsPayload
>;

export function useBulkEditAiTextTranslationSync(p: {
  resultsRef: MutableRefObject<Record<string, CountryGenResult>>;
  translateMutation: BulkEditTranslateMutation;
  setResultsByCountry: Dispatch<SetStateAction<Record<string, CountryGenResult>>>;
}) {
  const [translatingCountryKey, setTranslatingCountryKey] = useState<string | null>(null);
  const [translatingBatch, setTranslatingBatch] = useState<{ ck: string; batchIdx: number } | null>(null);

  const translateCountryTexts = useCallback(
    async (ck: string) => {
      const r = p.resultsRef.current[ck];
      if (!r || !countryGenResultHasTexts(r)) return;
      setTranslatingCountryKey(ck);
      try {
        const patch = await translateCountryGenResult(r, (pl) => p.translateMutation.mutateAsync(pl));
        p.setResultsByCountry((prev) => {
          const cur = prev[ck];
          if (!cur) return prev;
          return { ...prev, [ck]: { ...cur, ...patch } };
        });
      } finally {
        setTranslatingCountryKey(null);
      }
    },
    [p.translateMutation, p.setResultsByCountry, p.resultsRef],
  );

  const translateMissingInBatch = useCallback(
    async (ck: string, batchIdx: number) => {
      const r = p.resultsRef.current[ck];
      if (!r || !countryGenResultHasTexts(r)) return;
      setTranslatingBatch({ ck, batchIdx });
      try {
        const patch = await translateCountryGenResult(r, (pl) => p.translateMutation.mutateAsync(pl), {
          batchIndex: batchIdx,
        });
        p.setResultsByCountry((prev) => {
          const cur = prev[ck];
          if (!cur) return prev;
          return { ...prev, [ck]: { ...cur, ...patch } };
        });
      } finally {
        setTranslatingBatch(null);
      }
    },
    [p.translateMutation, p.setResultsByCountry, p.resultsRef],
  );

  return {
    translatingCountryKey,
    translatingBatch,
    translateCountryTexts,
    translateMissingInBatch,
  };
}
